/**
 * Validation utilities for forms and user input
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate team name
 */
export const validateTeamName = (teamName: string): ValidationResult => {
  if (!teamName.trim()) {
    return { isValid: false, error: 'Team name is required' };
  }
  
  if (teamName.trim().length < 2) {
    return { isValid: false, error: 'Team name must be at least 2 characters' };
  }
  
  if (teamName.trim().length > 50) {
    return { isValid: false, error: 'Team name must be less than 50 characters' };
  }
  
  return { isValid: true };
};

/**
 * Validate password (if required)
 */
export const validatePassword = (password: string, required: boolean = false): ValidationResult => {
  if (!required && !password) {
    return { isValid: true };
  }
  
  if (required && !password) {
    return { isValid: false, error: 'Password is required' };
  }
  
  if (password && password.length < 4) {
    return { isValid: false, error: 'Password must be at least 4 characters' };
  }
  
  return { isValid: true };
};

/**
 * Validate tile number
 */
export const validateTileNumber = (tileNumber: string): ValidationResult => {
  if (!tileNumber.trim()) {
    return { isValid: false, error: 'Tile number is required' };
  }
  
  const number = parseInt(tileNumber, 10);
  if (isNaN(number)) {
    return { isValid: false, error: 'Tile number must be a valid number' };
  }
  
  if (number < 1 || number > 999) {
    return { isValid: false, error: 'Tile number must be between 1 and 999' };
  }
  
  return { isValid: true };
};

/**
 * Validate answer text
 */
export const validateAnswerText = (answerText: string): ValidationResult => {
  if (!answerText.trim()) {
    return { isValid: false, error: 'Answer is required' };
  }
  
  if (answerText.trim().length > 500) {
    return { isValid: false, error: 'Answer must be less than 500 characters' };
  }
  
  return { isValid: true };
};

/**
 * Validate form data generically
 */
export const validateForm = <T extends Record<string, any>>(
  data: T,
  validators: Partial<Record<keyof T, (value: any) => ValidationResult>>
): { isValid: boolean; errors: Partial<Record<keyof T, string>> } => {
  const errors: Partial<Record<keyof T, string>> = {};
  let isValid = true;
  
  for (const [field, validator] of Object.entries(validators)) {
    if (validator) {
      const result = validator(data[field]);
      if (!result.isValid) {
        errors[field as keyof T] = result.error;
        isValid = false;
      }
    }
  }
  
  return { isValid, errors };
};

/**
 * Sanitize text input
 */
export const sanitizeText = (text: string): string => {
  return text.trim().replace(/[<>]/g, '');
};

/**
 * Check if input contains only alphanumeric characters and spaces
 */
export const isAlphanumericWithSpaces = (text: string): boolean => {
  return /^[a-zA-Z0-9\s]+$/.test(text);
};